import { createInspectorSearchParams } from "@/lib/inspector/navigation";
import { useNavigate } from "@tanstack/react-router";
import { Wrench } from "lucide-react";
import { EmptyState, InspectorSection, ToolItem } from "./InspectorSection";

interface Tool {
  name: string;
  description?: string;
}

interface ToolsSectionProps {
  tools: Tool[];
  projectId: string;
  isDeployed: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

export function ToolsSection({
  tools,
  projectId,
  isDeployed,
  isExpanded,
  onToggle,
}: ToolsSectionProps) {
  const navigate = useNavigate();

  const handleToolNavigate = (toolName: string) => {
    const searchParams = createInspectorSearchParams(toolName);
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: searchParams,
    });
  };

  const handleMoreToolsNavigate = () => {
    const firstTool = tools[0];
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: {
        tab: "inspector",
        selectedTool: firstTool?.name || undefined,
      },
    });
  };

  return (
    <InspectorSection
      title="Tools"
      icon={Wrench}
      count={isDeployed ? tools.length : 0}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {isDeployed && tools.length > 0 ? (
        <>
          {tools.slice(0, 3).map((tool, index) => (
            <ToolItem
              key={`${tool.name}-${index}`}
              tool={tool}
              onNavigate={handleToolNavigate}
            />
          ))}
          {tools.length > 3 && (
            <button
              type="button"
              onClick={handleMoreToolsNavigate}
              className="text-xs text-muted-foreground hover:text-primary transition-colors text-center pt-1 w-full"
            >
              +{tools.length - 3} more tools available
            </button>
          )}
        </>
      ) : (
        <EmptyState
          icon={Wrench}
          message={
            !isDeployed ? "Deploy to discover tools" : "No tools available"
          }
        />
      )}
    </InspectorSection>
  );
}
